import React, { useEffect } from 'react';
import {
	Alert,
	Box,
	Button,
	Checkbox,
	FormControlLabel,
	FormGroup,
	Step,
	StepLabel,
	Stepper,
	Typography,
} from '@mui/material';
import { Stack } from '@mui/system';
import * as Yup from 'yup';
import { useFormik, FormikProvider } from 'formik';
import { GridFields } from '../components/clients/GridFields';
import { registerClient, updateClient } from '../actions/client';
import { confirmModal, alertSweet } from '../helpers/sweetAlert2';
import {
	capLetters,
	getDivisonCodeByDivisionName,
	handleError,
} from '../helpers/utils';
import { Summary } from '../components/clients/Summary';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

const steps = [
	'Alta del cliente',
	'Datos generales',
	'Datos de facturación',
	'Datos de embarque',
	'Información adicional',
];

const excludedFieldsForReviewing = ['key', 'is_using_tax_domicile'];

const general_1 = [
	{ name: 'consultant', label: 'Asesor', text: 'Nombre completo' },
	{ name: 'company_name', label: 'Razón social', text: 'Nombre completo' },
	{
		name: 'last_4_digits_account',
		label: 'Últimos 4 dígitos de cuenta',
		maxLength: 4,
		text: null,
	},
	{ name: 'state', label: 'Estado', text: null },
	{ name: 'municipality', label: 'Municipio', text: null },
	{ name: 'city', label: 'Ciudad', text: null },
	{ name: 'address', label: 'Domicilio', text: null },
	{ name: 'zip_code', label: 'Código postal', maxLength: 5, text: null },
	{ name: 'neighborhood', label: 'Colonia', text: null },
	{ name: 'outside_number', label: 'Número exterior', text: null },
	{ name: 'interior_number', label: 'Número interior', text: null },
	{ name: 'rfc', label: 'RFC', maxLength: 13, text: null },
	{ name: 'credit', label: 'Crédito', text: 'Sí/No' },
	{ name: 'bank', label: 'Banco', text: null },
	{
		name: 'bank_you_are_depositing_to',
		label: 'Banco al que deposita',
		text: null,
	},
	{
		name: 'type',
		label: 'Tipo de cliente',
		text: null,
	},
];

const general_2 = [
	{
		name: 'purchase_contact',
		label: 'Contacto de compras',
		text: 'Nombre completo',
	},
	{
		name: 'phone_purchase',
		label: 'Teléfono de compras',
		maxLength: 12,
		text: 'Ejemplo: 522245533324',
	},
	{
		name: 'email_purchase',
		label: 'E-mail de compras',
		text: 'Ejemplo: ejemplo@empresa.com',
	},
	{
		name: 'accounts_payable_contact',
		label: 'Contacto de cuentas por pagar',
		text: 'Nombre completo',
	},
	{
		name: 'email_ap',
		label: 'E-mail de cuentas por pagar',
		text: 'Ejemplo: ejemplo@empresa.com',
	},
	{
		name: 'phone_ap',
		label: 'Teléfono de cuentas por pagar',
		maxLength: 12,
		text: 'Ejemplo: 522245533324',
	},
];

const billing_info = [
	{ name: 'use_of_cfdi', label: 'Uso del CFDI' },
	{ name: 'payment_method_1', label: 'Modo de pago' },
	{ name: 'payment_method_2', label: 'Método de pago' },
	{ name: 'payment_condition', label: 'Condición de pago' },
	{
		name: 'review_days',
		label: 'Días de revisión',
		text: 'Ejemplo: Lunes, Martes, Miércoles',
	},
	{ name: 'schedule', label: 'Horario', text: 'Ejemplo: 9 am a 5 pm' },
];

const shipping_info = [
	{ name: 'address_shipping', label: 'Domicilio de embarque' },
	{ name: 'municipality_shipping', label: 'Municipio de embarque' },
	{ name: 'colony_shipping', label: 'Colonia de embarque' },
	{
		name: 'zip_code_shipping',
		label: 'Código postal de embarque',
		maxLength: 5,
		text: null,
	},
	{
		name: 'delivery_days_shipping',
		label: 'Días de entrega',
		text: 'Ejemplo: Lunes, Martes, Miércoles',
	},
	{ name: 'hours_shipping', label: 'Horario', text: 'Ejemplo: 9 am a 5 pm' },
	{
		name: 'who_receives_shipping',
		label: 'Quién recibe',
		text: 'Nombre completo',
	},
	{
		name: 'phone_shipping',
		label: 'Teléfono de embarque',
		text: 'Ejemplo: 522245533324',
		maxLength: 12,
		text: 'Ejemplo: 522245533324',
	},
];

const additionl_info = [
	{
		name: 'additional_info',
		label: 'Información adicional',
		maxLength: 1000,
	},
];

const addStepToFields = (fieldsArray, step) => {
	const newStep = fieldsArray.map(field => ({ ...field, step: step }))
    return newStep;
};

const fields = [
    ...addStepToFields(general_1, 1),
    ...addStepToFields(general_2, 2),
    ...addStepToFields(billing_info, 3),
    ...addStepToFields(shipping_info, 4),
    ...addStepToFields(additionl_info, 5),
];

const createSchema = () => {
	let obj = {};
	fields.forEach((field) => {
		// if (field.name.includes('day')) {
		// 	obj[field.name] = Yup.array().required('Se requiere este campo');
		// } else {
		obj[field.name] = Yup.string()
			// .min(0, '¡Demasiado corto!')
			.max(field?.maxLength || 255, '¡Demasiado largo!')
			.nullable(true)
			.required((value) => {
				if (!value) {
					setActiveStep(field.step - 1);
					return 'Campo requerido';
				}
				return true;
			});
		// }
	});
	return obj;
};

const newValues = (finalValues, setFieldValue) => {
	let res = [];
	const original = fields.map((item) => ({
		label: item.label,
		name: item.name,
	}));
	let currentValue = '';
	original.forEach(({ label, name }) => {
		if (finalValues[name]?.length > 0) {
			currentValue = finalValues[name];
		} else {
			currentValue = '-';
			setFieldValue(name, currentValue);
			console.log(
				`Changed field "${name}" with new value "${currentValue}"`
			);
		}
		res.push({
			value: currentValue,
			title: label,
		});
	});
	return res;
};

const createInitialValues = (data) => {
	const autocompletes = [
		'state',
		'credit',
		'bank',
		'bank_you_are_depositing_to',
		'type',
	];
	let obj = data ? data : {};
	fields.map(({ name }) => {
		// if (name.includes('zip')) {
		// 	obj[name] = '52232';
		// } else if (name.includes('email')) {
		// 	obj[name] = 'example@hotmail.com';
		// } else if (name.includes('phone')) {
		// 	obj[name] = '522245533324';
		// } else if (name.includes('digits')) {
		// 	obj[name] = '5223';
		// } else if (autocompletes.includes(name)) {
		// 	obj[name] = null;
		// } else if (name.includes('day')) {
		// 	obj[name] = '';
		// } else {
		// 	obj[name] = 'Testing';
		// }

		if (autocompletes.includes(name)) {
			obj[name] = data ? data[name] : null;
		} else if (name.includes('day')) {
			obj[name] = data ? data[name] : [];
		} else {
			obj[name] = data ? data[name] : '';
		}
	});
	return obj;
};

// Formik schema
const RegisterSchema = Yup.object().shape(createSchema());

export const ClientsRegisterLocal = () => {
	const { state: locateParam = null } = useLocation();
	const navigate = useNavigate();
	const { user = null } = useSelector((state) => state);
	const initialValuesFormik = createInitialValues(locateParam?.clientData);
	const [activeStep, setActiveStep] = React.useState(0);
	const [alertErrorMsg, setAlertErrorMsg] = React.useState('');
	const [useLocalAddress, setUseLocalAddress] = React.useState(
		locateParam?.clientData
			? locateParam?.clientData?.is_using_tax_domicile
			: false
	);
	const [skipped, setSkipped] = React.useState(new Set());

	// Allow next step
	const [allowNextStep, setAllowNextStep] = React.useState(true);

	const isStepSkipped = (step) => {
		return skipped.has(step);
	};

	const handleNext = () => {
		let newSkipped = skipped;
		if (isStepSkipped(activeStep)) {
			newSkipped = new Set(newSkipped.values());
			newSkipped.delete(activeStep);
		}

		setActiveStep((prevActiveStep) => prevActiveStep + 1);
		setSkipped(newSkipped);
	};

	const handleBack = () => {
		setActiveStep((prevActiveStep) => prevActiveStep - 1);
	};

	const handleReset = () => {
		setActiveStep(0);
	};

	const handleUpdateClient = (data) => {
		updateClient(data.id, data)
			.then(() => {
				alertSweet('success', '¡Cliente actualizado!', 2);
				navigate('/dashboard/clientes');
			})
			.catch((error) => {
				handleTranslatedErrors(error);
				handleError(
					error,
					'Ocurrió un error al intentar actualizar al cliente. Verifica tus datos.'
				);
			});
	};

	const handleRegisterClient = (data) => {
		registerClient(data)
			.then(() => {
				confirmModal(
					'success',
					'¡Registro exitoso!',
					'Los datos se han guardado correctamente.'
				).then(({ isConfirmed, isDismissed }) => {
					if (isConfirmed || isDismissed) {
						// Reset stepper
						handleReset();
						// Reset form
						formik.resetForm(initialValuesFormik);
					}
				});
			})
			.catch((error) => {
				handleTranslatedErrors(error);
				handleError(
					error,
					'Ocurrió un error al intentar registrar al cliente. Verifica tus datos.'
				);
			});
	};

	const handleTranslatedErrors = (error) => {
		const errorData = error.response.data;
	
		// Common error message translations
		const errorTranslations = {
			"already exists.": "ya registrado.",
			"Enter a valid email address.": "inválido."
		};
	
		// Iterate over the error data
		for (const fieldName in errorData) {
			if (errorData?.hasOwnProperty(fieldName)) {
				// Find the corresponding field object
				const field = fields.find(f => f.name === fieldName);
				if (field) {
					// Set the active step
					setActiveStep(field.step - 1);
	
					// Get the error message
					const errorMessage = errorData[fieldName][0];
	
					// Translate the error message if possible
					let translatedMessage = errorMessage;
					for (const key in errorTranslations) {
						if (errorMessage?.includes(key)) {
							translatedMessage = `${field?.label}: ${errorTranslations[key]}`;
							break;
						}
					}
	
					// Set the alert error message
					setAlertErrorMsg(translatedMessage);
					break;
				}
			}
		}
	}

	const formik = useFormik({
		initialValues: initialValuesFormik,
		validationSchema: RegisterSchema,
		onSubmit: () => {
			try {
				const {
					rfc,
					consultant,
					company_name,
					address,
					neighborhood,
					municipality,
					state,
					city,
					bank,
					purchase_contact,
					accounts_payable_contact,
					colony_shipping,
					who_receives_shipping,
					type,
				} = formik.values;
				const data = {
					...formik.values,
					consultant: capLetters(consultant),
					company_name: company_name.toUpperCase(),
					neighborhood: capLetters(neighborhood),
					municipality: capLetters(municipality),
					state: capLetters(state),
					city: capLetters(city),
					bank: bank.toUpperCase(),
					address: capLetters(address),
					purchase_contact: capLetters(purchase_contact),
					accounts_payable_contact: capLetters(
						accounts_payable_contact
					),
					colony_shipping: capLetters(colony_shipping),
					who_receives_shipping: capLetters(who_receives_shipping),
					rfc: rfc.toUpperCase(),
					is_using_tax_domicile: useLocalAddress ? 1 : 0,
					type,
					// PHONES ARE NOT FORMATTED BECAUSE THEY CAN HAVE EXTENSION NUMBER
				};

				if (data?.division?.length > 3)
					data.division = getDivisonCodeByDivisionName(data.division);

				// If division not found put the user's division
				if (data.division === null) data.division = user.division;

				if (locateParam?.clientData) {
					handleUpdateClient(data);
				} else {
					handleRegisterClient(data);
				}
			} catch (error) {
				handleError(
					error,
					'Ocurrió un error al intentar formatear los datos del cliente.'
				);
			}
		},
	});

	const { handleSubmit, setFieldValue, values } = formik;

	useEffect(() => {
		console.log('useLocalAddress', useLocalAddress);
		let newAddress = `${values.address}. `;
		newAddress += `No. Ext.:${values.outside_number}. `;
		newAddress += `No. Int.:${values.interior_number}. `;
		newAddress += `${values.city}, ${values.state}.`;

		console.log(useLocalAddress ? newAddress : values?.address_shipping);

		// Set shipping info values
		setFieldValue(
			'address_shipping',
			useLocalAddress ? newAddress : values?.address_shipping
		);
		setFieldValue(
			'municipality_shipping',
			useLocalAddress
				? values.municipality
				: values?.municipality_shipping
		);
		setFieldValue(
			'colony_shipping',
			useLocalAddress ? values.neighborhood : values?.colony_shipping
		);
		setFieldValue(
			'zip_code_shipping',
			useLocalAddress ? values.zip_code : values?.zip_code_shipping
		);
	}, [useLocalAddress]);

	return (
		<FormikProvider value={formik}>
			{initialValuesFormik && (
				<Box sx={{ width: '100%' }}>
					<Stack
						direction='column'
						alignItems='flex-start'
						justifyContent='space-between'
						mb={1}
						style={{
							padding: '0 0 0 20px',
						}}
					>
						<Stack
							direction='row'
							alignItems='flex-end'
							justifyContent='space-between'
							mb={1}
							style={{
								padding: '20px 20px 0 0',
								width: '100%',
								display: 'flex',
								justifyContent: 'center',
								alignItems: 'center',
							}}
						>
							<Typography variant='h4' gutterBottom>
								Registro de nuevo cliente
							</Typography>
						</Stack>
						{!allowNextStep && (
							<div style={{ width: '60%', margin: '0 auto' }}>
								<Alert severity='error'>
									Hay campos incorrectos. Asegúrate de
									seleccionar solo las opciones permitidas en
									los campos de opción múltiple.
								</Alert>
							</div>
						)}
						{alertErrorMsg?.length > 0 && (
							<div style={{ width: '60%', margin: '0 auto' }}>
								<Alert severity='error'>{alertErrorMsg}</Alert>
							</div>
						)}
					</Stack>
					<Box
						sx={{
							borderBottom: 1,
							borderColor: 'divider',
							padding: '35px',
						}}
					>
						<Stepper activeStep={activeStep}>
							{steps.map((label, index) => {
								const stepProps = {};
								const labelProps = {};
								if (isStepSkipped(index)) {
									stepProps.completed = false;
								}
								return (
									<Step key={label} {...stepProps}>
										<StepLabel {...labelProps}>
											{label}
										</StepLabel>
									</Step>
								);
							})}
						</Stepper>
						{activeStep === steps?.length ? (
							<React.Fragment>
								<Typography variant='h6' sx={{ mt: 2, mb: 1 }}>
									<b>Resumen de la información.</b>
									<br />
									Todos los campos deben de ser completados,
									de lo contrario ningún pedido será
									autorizado.
								</Typography>
								<Summary
									values={newValues(
										formik.values,
										setFieldValue
									)}
								/>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										pt: 2,
									}}
								>
									<Box sx={{ flex: '1 1 auto' }} />
									<Button
										color='inherit'
										onClick={handleBack}
										sx={{ mr: 1 }}
									>
										Anterior
									</Button>
									<Button
										onClick={() => {
											handleSubmit();
										}}
									>
										{locateParam?.clientData
											? 'Editar'
											: 'Completar'}{' '}
										registro
									</Button>
								</Box>
							</React.Fragment>
						) : (
							<React.Fragment>
								{/* Content */}
								{activeStep === 0 && (
									<GridFields
										formik={formik}
										fields={general_1}
										setAllowNextStep={setAllowNextStep}
									/>
								)}

								{activeStep === 1 && (
									<GridFields
										formik={formik}
										fields={general_2}
									/>
								)}

								{activeStep === 2 && (
									<GridFields
										formik={formik}
										fields={billing_info}
									/>
								)}

								{activeStep === 3 && (
									<>
										<GridFields
											formik={formik}
											fields={shipping_info}
										/>

										<br />

										<FormGroup>
											<FormControlLabel
												control={
													<Checkbox
														checked={
															useLocalAddress
														}
														onClick={() =>
															setUseLocalAddress(
																!useLocalAddress
															)
														}
													/>
												}
												label='Utilizar domicilio fiscal'
											/>
										</FormGroup>
									</>
								)}

								{activeStep === 4 && (
									<GridFields
										formik={formik}
										fields={additionl_info}
									/>
								)}

								<Box
									sx={{
										display: 'flex',
										flexDirection: 'row',
										pt: 2,
									}}
								>
									<Button
										color='inherit'
										disabled={activeStep === 0}
										onClick={handleBack}
										sx={{ mr: 1 }}
									>
										Anterior
									</Button>
									<Box sx={{ flex: '1 1 auto' }} />

									<Button
										onClick={handleNext}
										disabled={!allowNextStep}
									>
										{activeStep === steps?.length - 1
											? 'Ver resumen'
											: 'Siguiente'}
									</Button>

									{locateParam && activeStep === 4 && (
										<Button
											onClick={() => {
												// Check if any field has empty value
												let emptyFields = Object.keys(
													values
												).filter((key) => !values[key]);
												// Remove excluded fields and get the label of the empty fields
												emptyFields = emptyFields
													.filter(
														(key) =>
															!excludedFieldsForReviewing.includes(
																key
															)
													)
													.map(
														(key) =>
															fields.find(
																(item) =>
																	item.name ===
																	key
															)
													);

												if (emptyFields.length > 0) {
													setActiveStep(emptyFields[0].step - 1 ?? 0)
													// Handle empty fields
													setAlertErrorMsg(
														`Los siguientes campos están incompletos: ${emptyFields?.map(empF => empF?.label).join(
															' , '
														)}`
													);
												} else {
													// Handle empty fields
													setAlertErrorMsg('');
													// Proceed with form submission
													handleSubmit();
												}
											}}
										>
											Guardar registro
										</Button>
									)}
								</Box>
							</React.Fragment>
						)}
					</Box>
				</Box>
			)}
		</FormikProvider>
	);
};

export const GridItem = ({ xs, title, text }) => (
	<Grid item xs={xs}>
		<Typography variant='p'>{title}</Typography>
		<Typography variant='h6'>{text}</Typography>
	</Grid>
);
